"use client";

import {
	VIDEO_HERO_IMAGES_EVENT,
	VIDEO_HERO_TEMPLATE_EVENT,
} from "@marketing/home/components/HeroControls";
import {
	EXAMPLE_ARROW_GUTTER,
	ExampleArrow,
} from "@saas/shared/components/ExampleArrow";
import { buildOptimizedImageUrl } from "@saas/shared/lib/image-url-builder";
import { cn } from "@ui/lib";
import { SparklesIcon, Volume2Icon, VolumeXIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { EFFECT_EXAMPLE_POSTER_WIDTH } from "../config";

export interface EffectExample {
	id: string;
	title: string;
	videoUrl: string;
	poster: string;
	orientation: "landscape" | "portrait";
	/** 「Try this example」按顺序填进左栏的固定槽位 */
	inputImages: readonly string[];
	/** 出这条片用的模板（AI Kiss 有三种吻）；「Try this example」顺带切过去，同样片区 */
	templateId?: string;
}

const THUMB_WIDTH = 256;

/**
 * 特效页右栏的 Examples：大预览 + 两侧箭头 + 缩略图条，版式同 /image-to-video 的示例区。
 * 预设页的提示词是藏起来的模板，所以没有那边的打字机入场；一条播完自动换下一条
 */
export function EffectExamples({
	examples,
	labels,
	onUse,
}: {
	examples: EffectExample[];
	labels: { title: string; use: string; mute: string; unmute: string };
	/** 填完槽位后调用：手机上表单在示例区上面，要滚回去 */
	onUse: () => void;
}) {
	const tAssets = useTranslations("assets");
	const [index, setIndex] = useState(0);
	// 浏览器只让静音视频自动播放
	const [isMuted, setIsMuted] = useState(true);
	const videoRef = useRef<HTMLVideoElement>(null);
	// 手机上示例区在整张表单下面：进了视野才拉流、开播，别一打开页面就在屏幕外下视频
	const { ref: inViewRef, inView } = useInView({ threshold: 0.3 });
	const example = examples[index];
	const exampleId = example?.id;
	const hasMultipleExamples = examples.length > 1;
	useEffect(() => {
		const video = videoRef.current;
		if (!video || !exampleId) {
			return;
		}
		if (inView) {
			video.play().catch(() => {});
		} else {
			video.pause();
		}
	}, [inView, exampleId]);
	if (!example) {
		return null;
	}
	const step = (delta: number) =>
		setIndex(
			(current) => (current + delta + examples.length) % examples.length,
		);
	const applyExample = (item: EffectExample) => {
		if (item.templateId) {
			window.dispatchEvent(
				new CustomEvent(VIDEO_HERO_TEMPLATE_EVENT, {
					detail: item.templateId,
				}),
			);
		}
		window.dispatchEvent(
			new CustomEvent(VIDEO_HERO_IMAGES_EVENT, {
				detail: [...item.inputImages],
			}),
		);
		onUse();
	};

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4 max-sm:px-0 sm:px-6 sm:py-6">
			<div className="@container mx-auto my-auto w-full max-w-[55rem]">
				<p
					className={cn(
						"mb-3 text-muted-foreground text-xs uppercase tracking-wider",
						EXAMPLE_ARROW_GUTTER,
					)}
				>
					{labels.title}
				</p>
				<div className={cn("relative", EXAMPLE_ARROW_GUTTER)}>
					{hasMultipleExamples ? (
						<ExampleArrow
							direction="prev"
							label={tAssets("prevExample")}
							onClick={() => step(-1)}
						/>
					) : null}
					<div
						ref={inViewRef}
						className="relative overflow-hidden rounded-2xl bg-black shadow-xl ring-1 ring-border/40"
					>
						<video
							key={example.id}
							ref={videoRef}
							src={example.videoUrl}
							poster={buildOptimizedImageUrl(
								example.poster,
								EFFECT_EXAMPLE_POSTER_WIDTH,
							)}
							preload={inView ? "auto" : "none"}
							muted={isMuted}
							playsInline
							loop={!hasMultipleExamples}
							onEnded={
								hasMultipleExamples ? () => step(1) : undefined
							}
							aria-label={example.title}
							className={cn(
								"w-full object-contain",
								example.orientation === "portrait"
									? "mx-auto aspect-[9/16] max-h-[60vh]"
									: "aspect-video",
							)}
						/>
						<button
							type="button"
							onClick={() => setIsMuted((muted) => !muted)}
							aria-label={isMuted ? labels.unmute : labels.mute}
							className="absolute right-3 bottom-3 z-10 rounded-full bg-black/50 p-2 text-white opacity-80 backdrop-blur-md transition-opacity hover:opacity-100"
						>
							{isMuted ? (
								<VolumeXIcon className="h-4 w-4" />
							) : (
								<Volume2Icon className="h-4 w-4" />
							)}
						</button>
					</div>
					{hasMultipleExamples ? (
						<ExampleArrow
							direction="next"
							label={tAssets("nextExample")}
							onClick={() => step(1)}
						/>
					) : null}
				</div>

				<div
					className={cn(
						"mt-4 flex flex-wrap items-center justify-between gap-3",
						EXAMPLE_ARROW_GUTTER,
					)}
				>
					<p className="min-w-0 font-medium text-sm text-zinc-800">
						{example.title}
					</p>
					{/* 外部借来的样片没有原图（Ozil Goal 的 mitte / aireel 片），点了只会把槽清空，不给这个按钮 */}
					{example.inputImages.length > 0 ? (
						<button
							type="button"
							onClick={() => applyExample(example)}
							className="flex items-center gap-1.5 rounded-full bg-blue-500 px-4 py-2 font-medium text-sm text-white shadow-sm transition-colors hover:bg-blue-600"
						>
							<SparklesIcon className="h-4 w-4" />
							{labels.use}
						</button>
					) : null}
				</div>

				{examples.length > 1 ? (
					<div className={cn("mt-4", EXAMPLE_ARROW_GUTTER)}>
						<div className="mx-auto flex w-max max-w-full gap-3 overflow-x-auto px-0.5 py-2 [&::-webkit-scrollbar]:hidden">
							{examples.map((item, itemIndex) => (
								<button
									key={item.id}
									type="button"
									onClick={() => setIndex(itemIndex)}
									aria-label={item.title}
									aria-pressed={itemIndex === index}
									className={cn(
										"relative aspect-square w-24 flex-shrink-0 overflow-hidden rounded-lg ring-2 ring-offset-2 transition-all sm:w-28",
										itemIndex === index
											? "ring-blue-500"
											: "ring-transparent hover:ring-border",
									)}
								>
									<img
										src={buildOptimizedImageUrl(
											item.poster,
											THUMB_WIDTH,
										)}
										alt=""
										loading="lazy"
										draggable={false}
										className="h-full w-full object-cover"
									/>
								</button>
							))}
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
}
